import type { ChoiceGroupSelections, DayOfWeek, DayPlan, MealSelections, WeekPlan } from "@/types/meal-plan-state";
import { choiceUnchanged } from "./week-sync";
import type { WeekOwner } from "./week-owner";

const WEEK_DAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const NO_DAY: DayPlan = { regimenId: null, meals: null };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** `cg` with exactly `eaten` (a copy), or without the key when `eaten` is undefined. */
function withEaten(cg: ChoiceGroupSelections, eaten: boolean[] | undefined): ChoiceGroupSelections {
  const { eaten: _dropped, ...rest } = cg;
  return eaten ? { ...rest, eaten: [...eaten] } : rest;
}

function preserveDayEaten(stored: DayPlan, incoming: DayPlan): DayPlan {
  if (!Array.isArray(incoming.meals)) return incoming;
  const meals = incoming.meals.map((meal, mi) =>
    Array.isArray(meal)
      ? meal.map((cg, ci) => {
          if (!isObject(cg)) return cg;
          const kept = choiceUnchanged(incoming, stored, mi, ci) ? stored.meals?.[mi]?.[ci]?.eaten : undefined;
          return withEaten(cg, Array.isArray(kept) ? kept : undefined);
        })
      : meal,
  );
  return { ...incoming, meals };
}

/**
 * "Comido" belongs to the week's owner: a planner can't change it (product
 * decision 2). Applied server-side to a save by anyone but the owner: every
 * choice group takes its `eaten` flags from `stored` (the owner's copy the
 * save is based on), whatever `incoming` says. A group whose food changed
 * (different picks or skip state, or a different regimen or menu for its
 * meal) starts unchecked, since the owner ate something else. With no stored
 * week, nothing is eaten.
 */
export function preserveOwnerEaten(stored: WeekPlan | null, incoming: WeekPlan): WeekPlan {
  const result = { ...incoming };
  for (const day of WEEK_DAYS) {
    const storedDay = stored && isObject(stored[day]) ? stored[day] : NO_DAY;
    result[day] = preserveDayEaten(storedDay, incoming[day]);
  }
  return result;
}

/** True when a choice group of `meals` (only meals `mealIndices`, when given) has a "comido" tick. */
export function hasEatenMarks(
  meals: readonly (MealSelections | null | undefined)[] | null | undefined,
  mealIndices?: Iterable<number>,
): boolean {
  if (!meals) return false;
  const indices = mealIndices ? [...mealIndices] : meals.keys();
  for (const mi of indices) {
    if (meals[mi]?.some((cg) => cg.eaten?.some(Boolean))) return true;
  }
  return false;
}

/**
 * For a confirm that resets meals: the sentence warning a planner that the
 * owner's "comido" ticks on them go too (the server clears ticks on changed
 * food, see `preserveOwnerEaten`). Null in my own weeks or when nothing is
 * ticked.
 */
export function eatenLossWarning(owner: WeekOwner, eaten: boolean): string | null {
  return owner.kind === "friend" && eaten ? `También se borrará lo que ${owner.username} marcó como comido.` : null;
}

/** `description` followed by `eatenLossWarning`, when there is one. */
export function withEatenLossWarning(description: string, owner: WeekOwner, eaten: boolean): string {
  const warning = eatenLossWarning(owner, eaten);
  return warning ? `${description} ${warning}` : description;
}
